import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tabs, Tag, Table, Space, Button, Typography, Modal, Input, message } from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface Creator {
  id: number;
  email: string;
  displayName: string | null;
  avatarCosKey: string | null;
  experimentBalanceCents: number;
  earningsBalanceCents: number;
  realNameVerified: boolean;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

interface DetailResp {
  creator: Creator;
  experimentCount: number;
  bundleCount: number;
}

interface Txn {
  id: number;
  walletType: string;
  kind: string;
  amountCents: number;
  balanceAfterCents: number;
  note: string | null;
  createdAt: string;
}

const fmtCents = (v: number) => {
  const sign = v < 0 ? '-' : '';
  return `${sign}¥${(Math.abs(v) / 100).toFixed(2)}`;
};

const CreatorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DetailResp | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [activeTab, setActiveTab] = useState<'experiment' | 'earnings'>('experiment');
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const loadDetail = async () => {
    const res = await request.get<DetailResp>(`/admin/creators/${id}`);
    setDetail(res.data as unknown as DetailResp);
  };

  const loadTxns = async (wallet: 'experiment' | 'earnings') => {
    const res = await request.get<{ items: Txn[] }>(`/admin/creators/${id}/transactions`, {
      params: { wallet, pageSize: 100 },
    });
    setTxns((res.data as unknown as { items: Txn[] }).items);
  };

  useEffect(() => {
    loadDetail();
    loadTxns(activeTab);
  }, [id, activeTab]);

  const onResetPassword = async () => {
    if (newPassword.length < 8) {
      message.error('密码至少 8 位');
      return;
    }
    await request.post(`/admin/creators/${id}/reset-password`, { newPassword });
    message.success('密码已重置');
    setResetOpen(false);
    setNewPassword('');
  };

  if (!detail) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {detail.creator.email}
        </Typography.Title>
        <Space>
          <Button onClick={() => navigate('/creators')}>返回列表</Button>
          <Button onClick={() => setResetOpen(true)}>重置密码</Button>
        </Space>
      </Space>
      <Card>
        <Descriptions column={3}>
          <Descriptions.Item label="ID">{detail.creator.id}</Descriptions.Item>
          <Descriptions.Item label="昵称">{detail.creator.displayName || '-'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag>{detail.creator.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="实验金">
            <Tag color="gold">{fmtCents(detail.creator.experimentBalanceCents)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="收益">
            <Tag color="green">{fmtCents(detail.creator.earningsBalanceCents)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="实名">
            {detail.creator.realNameVerified ? '已认证' : '未认证'}
          </Descriptions.Item>
          <Descriptions.Item label="实验数">{detail.experimentCount}</Descriptions.Item>
          <Descriptions.Item label="套图数">{detail.bundleCount}</Descriptions.Item>
          <Descriptions.Item label="注册时间">
            {dayjs(detail.creator.createdAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="上次登录">
            {detail.creator.lastLoginAt
              ? dayjs(detail.creator.lastLoginAt).format('YYYY-MM-DD HH:mm')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as 'experiment' | 'earnings')}
          items={[
            { key: 'experiment', label: '实验金流水' },
            { key: 'earnings', label: '收益流水' },
          ]}
        />
        <Table
          rowKey="id"
          dataSource={txns}
          columns={[
            { title: '类型', dataIndex: 'kind' },
            {
              title: '金额',
              dataIndex: 'amountCents',
              render: (v: number) => (
                <span style={{ color: v >= 0 ? '#15803D' : '#DC2626' }}>{fmtCents(v)}</span>
              ),
            },
            { title: '余额', dataIndex: 'balanceAfterCents', render: fmtCents },
            { title: '备注', dataIndex: 'note', render: (v) => v || '-' },
            {
              title: '时间',
              dataIndex: 'createdAt',
              render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
          ]}
          pagination={false}
        />
      </Card>
      <Modal
        open={resetOpen}
        title="重置密码"
        onOk={onResetPassword}
        onCancel={() => setResetOpen(false)}
      >
        <Input.Password
          placeholder="新密码（至少 8 位）"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>
    </Space>
  );
};

export default CreatorDetailPage;
